<template>
  <v-dialog :value="value" width="800px" :persistent="saving" @input="$emit('input', $event)">
    <v-card data-cy="meal-dialog">
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate/>
        <v-alert v-if="menuError" dense outlined type="error">{{ menuError }}</v-alert>

        <template v-if="isReplace">
          <p>
            {{ line.quantity }} of {{ line.description }}
            ({{ dateFormatted(line.date, {formatString: 'ddd MMM D'}) }}<template v-if="line.tod">, {{ line.tod }}</template>)
            for {{ profileName }}. The quantity stays the same.
          </p>
        </template>

        <template v-else>
          <v-select
            v-if="profiles.length > 1"
            v-model="profileUid"
            :items="profiles"
            :item-text="nameOf"
            item-value="uid"
            label="For"
            data-cy="meal-profile"
          />
          <v-tabs v-if="!isBox" v-model="kindTab" class="mb-4">
            <v-tab data-cy="kind-plan">Plan meal</v-tab>
            <v-tab data-cy="kind-substitute">Substitute meal</v-tab>
            <v-tab data-cy="kind-extra">Snacks &amp; extras</v-tab>
          </v-tabs>
          <v-row>
            <v-col v-if="isBox">
              <v-select
                v-model="deliveryDate"
                :items="deliveryDateItems"
                label="Box of the delivery on"
                data-cy="meal-delivery"
              />
            </v-col>
            <v-col v-if="needsDay">
              <v-select
                v-model="day"
                :items="mealDateItems"
                label="Day"
                data-cy="meal-day"
              />
            </v-col>
            <v-col v-if="kind === 'substitute'">
              <v-select
                v-model="tod"
                :items="['breakfast', 'lunch', 'dinner']"
                label="In place of the plan's"
                data-cy="meal-tod"
              />
            </v-col>
          </v-row>
        </template>

        <v-row>
          <v-col v-if="!isReplace" cols="2">
            <v-text-field v-model.number="quantity" label="Qty" type="number" min="1" data-cy="meal-quantity"/>
          </v-col>
          <v-col>
            <v-autocomplete
              v-model="meal"
              :items="mealOptions"
              item-text="optionText"
              item-value="optionKey"
              return-object
              :label="mealLabel"
              :no-data-text="noMealsText"
              data-cy="meal-select"
            />
          </v-col>
        </v-row>

        <v-checkbox
          v-if="!isReplace && isBox"
          v-model="keepBoxSize"
          hide-details
          label="Keep the box size, so the meals in the box keep their price"
          data-cy="meal-keep-box-size"
        />
        <div v-if="!isReplace && isBox && !keepBoxSize" class="text-caption ml-8" data-cy="meal-box-resize-note">
          The box becomes the smallest size on the plan that fits its meals, and they are priced at that size.
        </div>
        <v-checkbox
          v-if="kind === 'extra'"
          v-model="isOnlyOnce"
          hide-details
          label="Once only: this week, not carried into the customer's later weeks"
          data-cy="meal-only-once"
        />
        <div v-if="kind === 'plan' && !isReplace && !isBox" class="text-caption">
          Added to what is ordered for that day and time of day. A slot that holds a substitute is changed with Replace.
        </div>
        <v-checkbox
          v-if="isSubstitution"
          v-model="isAllergySubstitution"
          hide-details
          label="Allergy substitution: charged as the plan's own meal"
          data-cy="meal-allergy"
        />
        <v-checkbox
          v-model="sendEmail"
          hide-details
          label="Send the customer the order-updated email"
          data-cy="meal-send-email"
        />
      </v-card-text>
      <v-card-actions>
        <v-btn text :disabled="saving" @click="$emit('input', false)">Close</v-btn>
        <v-spacer/>
        <v-btn
          v-if="isReplace && line.is_substitution"
          outlined
          :disabled="saving"
          data-cy="meal-plan-again"
          @click="replaceByPlanMeal"
        >
          Back to the plan's own meal
        </v-btn>
        <v-btn color="primary" :disabled="!canSave" :loading="saving" data-cy="meal-save" @click="save">
          {{ isReplace ? 'Replace' : 'Add' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {dateFormatted, formatCurrency} from '@/store/utils';
import {
  boxMealOptions, deliveryDateOfMealDate, deliveryDatesOf, extraOptions, mealDatesOf, planMealOptions,
  substituteOptions, toCancelLine
} from '@/utils/order-edit';

const KINDS = ['plan', 'substitute', 'extra'];

// The dialog of the Order Edit page that adds a meal to a customer's
// order (no `line`) or replaces the meal of one invoice line (`line`). It only
// works out what to send: the page makes the change.
export default {
  name: 'OrderMealDialog',
  props: {
    value: {type: Boolean, required: true},
    week: {type: String, required: true},
    // the household's profiles: {uid, first_name, last_name, stream, ordersAsBox}
    profiles: {type: Array, required: true},
    // the profile a meal is added for when the dialog opens
    defaultUid: {type: String, default: null},
    // the invoice line to replace the meal of; null to add a meal
    line: {type: Object, default: null},
    saving: {type: Boolean, default: false},
  },
  data() {
    return {
      loading: false,
      menuError: null,
      // the week's menu by stream, and the personal menu of the profile when its stream is `personal`
      menus: {},
      personalMenu: null,
      profileUid: null,
      kindTab: 0,
      deliveryDate: null,
      day: null,
      tod: 'lunch',
      quantity: 1,
      meal: null,
      isAllergySubstitution: false,
      // an extra is added for this week only, unless unticked
      isOnlyOnce: true,
      // a box becomes the size that fits its meals, unless this is ticked
      keepBoxSize: false,
      sendEmail: false,
    };
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.resetForm();
        return this.loadMenus();
      }
    },
    profileUid() {
      this.meal = null;
      return this.loadPersonalMenu();
    },
    kindTab() {
      this.meal = null;
    },
    day() {
      this.meal = null;
    },
    deliveryDate() {
      this.meal = null;
    },
  },
  computed: {
    isReplace() {
      return !!this.line;
    },
    profile() {
      const uid = this.isReplace ? this.line.uid : this.profileUid;
      return this.profiles.find(profile => profile.uid === uid) || null;
    },
    profileName() {
      return this.profile ? this.nameOf(this.profile) : '';
    },
    isBox() {
      return this.isReplace ? this.line.order_type === 'box' : !!this.profile && this.profile.ordersAsBox;
    },
    // what is added to a meals order: a plan meal, a substitute for one, or an extra
    kind() {
      if (this.isBox) {
        return 'box';
      }
      return this.isReplace ? 'substitute' : KINDS[this.kindTab];
    },
    isSubstitution() {
      return this.kind === 'substitute';
    },
    needsDay() {
      return !this.isReplace && (this.kind === 'substitute' || this.kind === 'extra');
    },
    mealDates() {
      return mealDatesOf(this.menus);
    },
    deliveryDates() {
      return deliveryDatesOf(this.menus);
    },
    // as select items: a select shows a plain date as it is, whatever its item-text
    mealDateItems() {
      return this.mealDates.map(date => ({text: this.formatDay(date), value: date}));
    },
    deliveryDateItems() {
      return this.deliveryDates.map(date => ({text: this.formatDay(date), value: date}));
    },
    ownStreamMenu() {
      if (!this.profile) {
        return null;
      }
      return this.profile.stream === 'personal' ? this.personalMenu : this.menus[this.profile.stream];
    },
    // the delivery the chosen day, or the line being replaced, ships with
    shipsWithDelivery() {
      if (this.isBox) {
        return this.isReplace ? this.line.date : this.deliveryDate;
      }
      return deliveryDateOfMealDate(this.menus, this.isReplace ? this.line.date : this.day);
    },
    meals() {
      const ownStream = this.profile ? this.profile.stream : null;
      if (this.kind === 'box') {
        return boxMealOptions(this.menus, this.shipsWithDelivery);
      }
      if (this.kind === 'plan') {
        return planMealOptions(this.ownStreamMenu);
      }
      if (this.kind === 'substitute') {
        return substituteOptions(this.menus, ownStream, this.shipsWithDelivery);
      }
      return this.shipsWithDelivery ? extraOptions(this.menus, this.shipsWithDelivery) : [];
    },
    mealOptions() {
      const isMealOfLine = meal => this.isReplace && `${meal.id}`.replace(/CC$/, '') === `${this.line.meal_id}`.replace(/CC$/, '');
      return this.meals
        .filter(meal => !isMealOfLine(meal))
        .map(meal => ({...meal, optionKey: `${meal.id}|${meal.date}|${meal.tod}`, optionText: this.describeMeal(meal)}));
    },
    mealLabel() {
      if (this.isReplace) {
        return 'Replace by';
      }
      return this.kind === 'extra' ? 'Snack or extra' : 'Meal';
    },
    noMealsText() {
      if (this.loading) {
        return 'Loading the menu…';
      }
      return this.needsDay && !this.day ? 'Choose the day first' : 'No meals on the menu for this';
    },
    title() {
      if (this.isReplace) {
        return 'Replace meal';
      }
      return `Add meal${this.profile && this.profile.stream ? ` (${this.profile.stream})` : ''}`;
    },
    canSave() {
      const hasQuantity = this.isReplace || (Number.isInteger(this.quantity) && this.quantity > 0);
      return !this.saving && !!this.meal && !!this.profile && hasQuantity;
    },
  },
  methods: {
    dateFormatted,
    nameOf(profile) {
      return [profile.first_name, profile.last_name].filter(s => !!s).join(' ') || profile.uid;
    },
    formatDay(date) {
      return dateFormatted(date, {formatString: 'dddd MMM D'});
    },
    describeMeal(meal) {
      if (meal.chefs_choice || !meal.date) {
        const price = meal.price ? `, ${formatCurrency(meal.price)}` : '';
        return `${meal.name} (${meal.tod || meal.category || 'extra'}${price})`;
      }
      return `${dateFormatted(meal.date, {formatString: 'ddd MMM D'})} · ${meal.tod} · ${meal.name} (${meal.stream})`;
    },
    resetForm() {
      const defaultProfile = this.profiles.find(profile => profile.uid === this.defaultUid) || this.profiles[0];
      this.profileUid = defaultProfile ? defaultProfile.uid : null;
      this.kindTab = 0;
      this.deliveryDate = null;
      this.day = null;
      this.tod = 'lunch';
      this.quantity = 1;
      this.meal = null;
      this.isAllergySubstitution = false;
      this.isOnlyOnce = true;
      this.keepBoxSize = false;
      this.sendEmail = false;
    },
    async loadMenus() {
      this.loading = true;
      this.menuError = null;
      try {
        this.menus = (await this.$store.dispatch('fetchMenu', this.week)) || {};
        await this.loadPersonalMenu();
        if (this.deliveryDates.length === 0) {
          this.menuError = 'There is no menu for this week yet.';
        }
      } catch (e) {
        console.error('failed to load the menu', e);
        this.menuError = `The menu could not be loaded: ${e.message}`;
      } finally {
        this.loading = false;
      }
    },
    async loadPersonalMenu() {
      this.personalMenu = null;
      if (this.profile && this.profile.stream === 'personal' && !this.isBox) {
        this.personalMenu = await this.$store.dispatch('fetchPersonalMenu', {date: this.week, uid: this.profile.uid});
      }
    },
    // the meal as the api adds it, by what is being added
    toAddLine() {
      const {meal, quantity} = this;
      const common = {uid: this.profile.uid, meal_id: meal.id, quantity, description: meal.name};
      if (this.kind === 'box') {
        return {...common, order_type: 'box', date: this.deliveryDate, stream: meal.stream, chefs_choice: !!meal.chefs_choice};
      }
      if (this.kind === 'plan') {
        const order_type = meal.order_type === 'flex' ? 'flex' : 'full';
        return {...common, order_type, date: meal.date, tod: meal.tod, stream: meal.stream};
      }
      if (this.kind === 'substitute') {
        return {
          ...common, order_type: 'full', date: this.day, tod: this.tod, stream: meal.stream,
          is_substitution: true, is_allergy_substitution: this.isAllergySubstitution,
        };
      }
      return {
        ...common, order_type: 'chefs_choice', date: this.day, tod: meal.tod || meal.category, only_once: this.isOnlyOnce,
      };
    },
    save() {
      if (!this.isReplace) {
        this.$emit('add', {
          meals: [this.toAddLine()],
          sendEmail: this.sendEmail,
          keepBoxSize: this.isBox && this.keepBoxSize,
        });
        return;
      }
      const {meal} = this;
      this.$emit('replace', {
        line: toCancelLine(this.line, this.line.quantity),
        replacement: {
          meal_id: meal.id,
          stream: meal.stream,
          description: meal.name,
          chefs_choice: !!meal.chefs_choice,
          is_allergy_substitution: this.isSubstitution && this.isAllergySubstitution,
        },
        sendEmail: this.sendEmail,
      });
    },
    replaceByPlanMeal() {
      this.$emit('replace', {
        line: toCancelLine(this.line, this.line.quantity),
        replacement: {meal_id: null, description: "the plan's own meal"},
        sendEmail: this.sendEmail,
      });
    },
  },
}
</script>
